import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { Clock, Users, MapPin, Edit, Trash2, CheckCircle, UserX, ChevronLeft, ChevronRight, MessageSquare, Globe, UtensilsCrossed, UserPlus, Phone, User, AlertCircle, RotateCcw, MoreVertical, Calendar, XCircle, Ghost } from "lucide-react";
import ScrollContainer from "react-indiana-drag-scroll";
// Button import removed - using native button elements in compact layout
import { cn } from "../../lib/utils";
import {
  getCustomerNotesForDisplay,
  getCustomerPhoneForDisplay,
  getReservationNotesForDisplay,
  reservationHasUserFacingNotes,
} from "../../lib/customerClassification";
import type { Reservation, ReservationTimelineData } from "../../types";

interface ReservationTimelineProps {
  timelineData: ReservationTimelineData;
  onEditReservation?: (reservation: Reservation) => void;
  onDeleteReservation?: (reservationId: string) => void;
  onCheckInReservation?: (reservationId: string) => void;
  onCompleteReservation?: (reservationId: string) => void;
  onReopenReservation?: (reservationId: string) => void;
  onMarkNoShow?: (reservationId: string) => void;
  selectedShift?: string;
  onShiftChange?: (shiftId: string) => void;
  onDateChange?: (date: string) => void;
}

// Time slots are now dynamically generated based on shift data in the main component

// Status colors for reservations
const getStatusColor = (status: Reservation["status"]) => {
  switch (status) {
    case "confirmed":
      return "bg-blue-300 border-blue-500 text-blue-900";
    case "in-progress":
      return "bg-amber-200 border-amber-400 text-amber-900";
    case "completed":
      return "bg-green-300 border-green-500 text-green-900";
    case "cancelled":
      return "bg-red-300 border-red-500 text-red-900";
    case "no-show":
      return "bg-gray-300 border-gray-500 text-gray-900";
    default:
      return "bg-purple-300 border-purple-500 text-purple-900";
  }
};

const getStatusText = (status: Reservation["status"]) => {
  switch (status) {
    case "confirmed":
      return "Επιβεβαιωμένη";
    case "in-progress":
      return "Σε εξέλιξη";
    case "completed":
      return "Ολοκληρωμένη";
    case "cancelled":
      return "Ακυρωμένη";
    case "no-show":
      return "Δεν εμφανίστηκε";
    default:
      return status;
  }
};

// Status icons for compact cards - using Lucide icons for consistency
const getStatusIcon = (status: Reservation["status"]) => {
  switch (status) {
    case "confirmed":
      return Calendar;
    case "in-progress":
      return UtensilsCrossed;
    case "completed": 
      return CheckCircle;
    case "cancelled":
      return XCircle;
    case "no-show":
      return Ghost;
    default:
      return Clock;
  }
};

// Source icon mapping
type ReservationSource = 'online' | 'manual' | 'phone' | 'walk-in' | 'unknown';

const SOURCE_ICONS: Record<ReservationSource, React.ElementType> = {
  online: Globe,
  manual: UtensilsCrossed,
  phone: Phone,
  'walk-in': UserPlus,
  unknown: UtensilsCrossed,
};

const SOURCE_LABELS: Record<ReservationSource, string> = {
  online: 'Online',
  manual: 'Εσωτερικό',
  phone: 'Τηλέφωνο',
  'walk-in': 'Walk-in',
  unknown: 'Άγνωστο',
};

const getReservationTableLabel = (reservation: Reservation): string | null => {
  if (Array.isArray(reservation.tables) && reservation.tables.length > 1) {
    return `T${reservation.tables.map((table) => table.number).join(', T')}`;
  }
  if (reservation.table?.number) {
    return `T${reservation.table.number}`;
  }
  return null;
};

// Shared helper for no-show visibility (used in card + action sheet)
const shouldShowNoShow = (reservation: Reservation): boolean => {
  if (reservation.status !== 'confirmed') return false;
  const now = new Date();
  const reservationDateTime = new Date(`${reservation.date}T${reservation.time}:00`);
  const graceMs = 15 * 60 * 1000;
  return now.getTime() > reservationDateTime.getTime() + graceMs;
};

interface ReservationCardProps {
  reservation: Reservation;
  onEdit?: (reservation: Reservation) => void;
  onDelete?: (reservationId: string) => void;
  onCheckIn?: (reservationId: string) => void;
  onComplete?: (reservationId: string) => void;
  onMarkNoShow?: (reservationId: string) => void;
  onLongPress?: (reservation: Reservation) => void;
}

// Compact version for horizontal timeline
function ReservationCardCompact({ reservation, onEdit, onDelete, onCheckIn, onComplete, onMarkNoShow, onLongPress }: ReservationCardProps) {
  // Long press detection for mobile without interfering with horizontal drag
  const pressTimer = useRef<number | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const [useOverflowMenu, setUseOverflowMenu] = useState(false);

  // Detect screen size for overflow menu pattern
  useEffect(() => {
    const checkScreenSize = () => {
      // Show overflow menu on mobile/tablet (< 1024px), full buttons on desktop
      setUseOverflowMenu(window.innerWidth < 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const clearPress = () => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    startPos.current = null;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return; // only on touch
    startPos.current = { x: e.clientX, y: e.clientY };
    pressTimer.current = window.setTimeout(() => {
      pressTimer.current = null;
      onLongPress?.(reservation);
    }, 450); // long press threshold
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!pressTimer.current || !startPos.current) return;
    const dx = Math.abs(e.clientX - startPos.current.x);
    const dy = Math.abs(e.clientY - startPos.current.y);
    if (dx > 10 || dy > 10) {
      clearPress(); // treat as scroll/drag, cancel long press
    }
  };

  const handlePointerUp = () => clearPress();
  const handlePointerCancel = () => clearPress();
  const handleDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button")) return;
    onLongPress?.(reservation);
  };

  const canCheckIn = reservation.status === "confirmed" && !!onCheckIn;
  const canComplete = reservation.status === "in-progress" && !!onComplete;
  const canMarkNoShow = reservation.status === "confirmed" && shouldShowNoShow(reservation) && !!onMarkNoShow;
  const canEdit = !!onEdit;
  const canDelete =
    !!onDelete &&
    reservation.status !== "cancelled" &&
    reservation.status !== "completed" &&
    reservation.status !== "no-show";

  const actionButtons = [
    canCheckIn && {
      key: "check-in",
      title: "Check-in",
      className: "w-6 h-6 bg-green-50 hover:bg-green-100 border border-green-300 rounded flex items-center justify-center relative z-10 transition-colors",
      icon: <CheckCircle className="h-3.5 w-3.5 text-green-600" />,
      onClick: () => onCheckIn?.(reservation.id),
    },
    canComplete && {
      key: "complete",
      title: "Ολοκληρώθηκε",
      className: "w-6 h-6 bg-purple-50 hover:bg-purple-100 border border-purple-300 rounded flex items-center justify-center relative z-10 transition-colors",
      icon: <CheckCircle className="h-3.5 w-3.5 text-purple-600" />,
      onClick: () => onComplete?.(reservation.id),
    },
    canMarkNoShow && {
      key: "no-show",
      title: "Σήμανση ως Δεν εμφανίστηκε",
      className: "w-6 h-6 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded flex items-center justify-center relative z-10 transition-colors",
      icon: <UserX className="h-3.5 w-3.5 text-gray-600" />,
      onClick: () => onMarkNoShow?.(reservation.id),
    },
    canEdit && {
      key: "edit",
      title: "Edit",
      className: "w-6 h-6 bg-blue-50 hover:bg-blue-100 border border-blue-300 rounded flex items-center justify-center relative z-10 transition-colors",
      icon: <Edit className="h-3.5 w-3.5 text-blue-600" />,
      onClick: () => onEdit?.(reservation),
    },
    canDelete && {
      key: "delete",
      title: "Delete",
      className: "w-6 h-6 bg-red-50 hover:bg-red-100 border border-red-300 rounded flex items-center justify-center relative z-10 transition-colors",
      icon: <Trash2 className="h-3.5 w-3.5 text-red-600" />,
      onClick: () => onDelete?.(reservation.id),
    },
  ].filter(Boolean) as Array<{
    key: string;
    title: string;
    className: string;
    icon: JSX.Element;
    onClick: () => void;
  }>;

  // On mobile/tablet: show overflow menu (More button). On desktop: show all action buttons.
  const actionColumnWidth = useOverflowMenu ? 64 : Math.max(64, actionButtons.length > 0 ? actionButtons.length * 28 - 4 : 64);

  return (
    <div
      id={`reservation-card-${reservation.id}`}
      data-reservation-card
      className={cn(
        "w-52 xl:w-64 2xl:w-72 h-16 p-2 rounded-lg border-2 shadow-sm transition-[box-shadow,transform] duration-200 hover:shadow-md relative bg-white flex-shrink-0",
        getStatusColor(reservation.status)
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onDoubleClick={handleDoubleClick}
      title={useOverflowMenu ? undefined : "Διπλό κλικ για λεπτομέρειες"}
    >
      <div className="flex items-start h-full">
        <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
          <h4 className="font-semibold text-xs truncate">
            {reservation.customer?.name || "Unknown Customer"}
          </h4>
          <div className="flex items-center gap-1.5 text-sm">
            <Users className="h-4 w-4 text-gray-700" />
            <span className="font-semibold text-gray-700">
              {reservation.party_size}
            </span>
            {reservationHasUserFacingNotes(reservation) && (
              <span
                className="inline-flex items-center ml-0.5"
                title="Υπάρχουν σημειώσεις"
              >
                <AlertCircle
                  className="h-4 w-4 text-orange-500 dark:text-orange-400"
                  style={{ filter: "drop-shadow(0 0 3px rgba(249, 115, 22, 0.6))" }}
                />
              </span>
            )}
            {getReservationTableLabel(reservation) && (
              <>
                <MapPin className="h-3.5 w-3.5 ml-1 text-gray-700" />
                <span className="font-semibold text-gray-700">
                  {getReservationTableLabel(reservation)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex-none flex flex-col justify-between items-end h-full ml-2.5" style={{ width: `${actionColumnWidth}px` }}>
          <div
            className="w-7 h-7 flex items-center justify-center rounded-md bg-opacity-20"
            title={getStatusText(reservation.status)}
          >
            {(() => {
              const StatusIcon = getStatusIcon(reservation.status);
              const iconColorClass = reservation.status === "confirmed" ? "text-blue-700" :
                reservation.status === "in-progress" ? "text-amber-700" :
                reservation.status === "completed" ? "text-green-700" :
                reservation.status === "cancelled" ? "text-red-700" :
                reservation.status === "no-show" ? "text-gray-700" : "text-purple-700";
              return <StatusIcon className={`h-4 w-4 ${iconColorClass}`} strokeWidth={2} />;
            })()}
          </div>
          <div className="flex items-center justify-end gap-1">
            {useOverflowMenu ? (
              // Mobile/tablet: single More button that opens action sheet
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onLongPress?.(reservation);
                }}
                className="w-7 h-7 rounded-lg flex items-center justify-center relative z-10 transition-[background-color,opacity] duration-150 bg-black/8 hover:bg-black/12 dark:bg-white/15 dark:hover:bg-white/25 text-current opacity-80 hover:opacity-100"
                title="Περισσότερες ενέργειες"
              >
                <MoreVertical className="h-4 w-4" strokeWidth={2} />
              </button>
            ) : (
              // Desktop: full action buttons
              actionButtons.map((action) => (
                <button
                  key={action.key}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    action.onClick();
                  }}
                  className={action.className}
                  title={action.title}
                >
                  {action.icon}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Scrollable Time Slot Component with Navigation Arrows (PC only)
interface ScrollableTimeSlotProps {
  timeSlot: {
    time: string;
    available: boolean;
    reservations: Reservation[];
  };
  onEditReservation?: (reservation: Reservation) => void;
  onDeleteReservation?: (reservationId: string) => void;
  onCheckInReservation?: (reservationId: string) => void;
  onCompleteReservation?: (reservationId: string) => void;
  onMarkNoShow?: (reservationId: string) => void;
  onLongPressReservation?: (reservation: Reservation) => void;
}

function ScrollableTimeSlot({
  timeSlot,
  onEditReservation,
  onDeleteReservation,
  onCheckInReservation,
  onCompleteReservation,
  onMarkNoShow,
  onLongPressReservation,
}: ScrollableTimeSlotProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isPcView, setIsPcView] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  // Check if we're in PC view (screen width > 768px)
  useEffect(() => {
    const checkScreenSize = () => {
      setIsPcView(window.innerWidth > 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Detect touch devices to prefer native scrolling
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      setIsTouch(window.matchMedia('(pointer: coarse)').matches);
    }
  }, []);

  const checkScrollState = () => {
    // Try to find the ScrollContainer element first, then look for scrollable children
    const wrapper = scrollContainerRef.current;
    if (!wrapper) return;
    
    // The ScrollContainer creates a child div that's scrollable
    const containers = wrapper.getElementsByTagName('div');
    let container: HTMLElement | null = null;
    
    // Find the first div that has horizontal scroll
    for (let i = 0; i < containers.length; i++) {
      const el = containers[i];
      if (el.scrollWidth > el.clientWidth || el.style.overflow?.includes('auto') || el.style.overflow?.includes('scroll')) {
        container = el;
        break;
      }
    }
    
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const canLeft = scrollLeft > 1; // Small threshold to account for rounding
      const canRight = scrollLeft < scrollWidth - clientWidth - 1;
      
      
      setCanScrollLeft(canLeft);
      setCanScrollRight(canRight);
    }
  };

  // Initial scroll state check
  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollState();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [timeSlot.reservations]);


  const smoothScrollTo = (container: HTMLElement, targetScroll: number, duration: number = 200) => {
    const startScroll = container.scrollLeft;
    const distance = targetScroll - startScroll;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      container.scrollLeft = startScroll + (distance * easeOut);
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  const scrollLeft = () => {
    // Try to find the ScrollContainer element first, then look for scrollable children
    const wrapper = scrollContainerRef.current;
    if (!wrapper) return;
    
    // The ScrollContainer creates a child div that's scrollable
    const containers = wrapper.getElementsByTagName('div');
    let container: HTMLElement | null = null;
    
    // Find the first div that has horizontal scroll
    for (let i = 0; i < containers.length; i++) {
      const el = containers[i];
      if (el.scrollWidth > el.clientWidth || el.style.overflow?.includes('auto') || el.style.overflow?.includes('scroll')) {
        container = el;
        break;
      }
    }
    
    if (container) {
      const currentScroll = container.scrollLeft;
      const targetScroll = Math.max(0, currentScroll - 200);
      smoothScrollTo(container, targetScroll);
      
      // Update scroll state after animation
      setTimeout(() => {
        checkScrollState();
      }, 250);
    }
  };

  const scrollRight = () => {
    // Try to find the ScrollContainer element first, then look for scrollable children
    const wrapper = scrollContainerRef.current;
    if (!wrapper) return;
    
    // The ScrollContainer creates a child div that's scrollable
    const containers = wrapper.getElementsByTagName('div');
    let container: HTMLElement | null = null;
    
    // Find the first div that has horizontal scroll
    for (let i = 0; i < containers.length; i++) {
      const el = containers[i];
      if (el.scrollWidth > el.clientWidth || el.style.overflow?.includes('auto') || el.style.overflow?.includes('scroll')) {
        container = el;
        break;
      }
    }
    
    if (container) {
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const targetScroll = Math.min(maxScroll, currentScroll + 200);
      smoothScrollTo(container, targetScroll);
      
      // Update scroll state after animation
      setTimeout(() => {
        checkScrollState();
      }, 250);
    }
  };


  if (timeSlot.reservations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-xs text-muted-foreground border-2 border-dashed border-border rounded px-3 py-1 bg-accent">
          Κενό
        </div>
      </div>
    );
  }

  return (
    <div className="h-full relative">
      {/* Navigation Arrows - PC Only */}
      {isPcView && timeSlot.reservations.length > 1 && (
        <>
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-50 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full p-1.5 shadow-lg transition-[background-color,box-shadow,transform] duration-200 hover:shadow-xl active:scale-95"
              title="Προηγούμενες κρατήσεις"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
          )}
          
          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-50 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full p-1.5 shadow-lg transition-[background-color,box-shadow,transform] duration-200 hover:shadow-xl active:scale-95"
              title="Επόμενες κρατήσεις"
            >
              <ChevronRight className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
          )}
        </>
      )}

      {/* Scrollable Content */}
      <div ref={scrollContainerRef}>
        {isTouch ? (
          <div
            className="h-full overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            style={{ scrollbarWidth: 'thin', touchAction: 'pan-x pan-y' }}
            onScroll={() => checkScrollState()}
          >
            <div className="flex gap-2 h-full py-2 px-3 min-w-max">
              {timeSlot.reservations.map((reservation) => (
                <div key={reservation.id} className="flex-shrink-0">
                  <ReservationCardCompact
                    reservation={reservation}
                    onEdit={onEditReservation}
                    onDelete={onDeleteReservation}
                    onCheckIn={onCheckInReservation}
                    onComplete={onCompleteReservation}
                    onMarkNoShow={onMarkNoShow}
                    onLongPress={onLongPressReservation}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <ScrollContainer 
            className="h-full overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            style={{ scrollbarWidth: 'thin', touchAction: 'pan-x pan-y' }}
            hideScrollbars={false}
            activationDistance={25}
            nativeMobileScroll
            onScroll={() => {
              checkScrollState();
            }}
            onStartScroll={() => {
              // Force check scroll state when drag starts
              setTimeout(() => {
                checkScrollState();
              }, 50);
            }}
          >
          <div className="flex gap-2 h-full py-2 px-3 min-w-max">
            {timeSlot.reservations.map((reservation) => (
              <div key={reservation.id} className="flex-shrink-0">
                <ReservationCardCompact
                  reservation={reservation}
                  onEdit={onEditReservation}
                  onDelete={onDeleteReservation}
                  onCheckIn={onCheckInReservation}
                  onComplete={onCompleteReservation}
                  onMarkNoShow={onMarkNoShow}
                  onLongPress={onLongPressReservation}
                />
              </div>
            ))}
          </div>
          </ScrollContainer>
        )}
      </div>
    </div>
  );
}

// Original ReservationCard removed - using ReservationCardCompact for horizontal layout

export function ReservationTimeline({
  timelineData,
  onEditReservation,
  onDeleteReservation,
  onCheckInReservation,
  onCompleteReservation,
  onReopenReservation,
  onMarkNoShow,
  selectedShift,
  onDateChange,
  // onShiftChange, // Unused parameter
}: ReservationTimelineProps) {
  const [actionSheetReservation, setActionSheetReservation] = useState<Reservation | null>(null);

  const closeActionSheet = () => setActionSheetReservation(null);
  const openActionSheet = (reservation: Reservation) => {
    setActionSheetReservation(reservation);
  };
  // Filter timeline data for selected shift
  const filteredShifts = selectedShift
    ? timelineData.shifts.filter(s => s.shift.id === selectedShift)
    : timelineData.shifts;

  const currentShift = filteredShifts[0];

  if (!currentShift) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <p>{timelineData.shifts.length === 0 ? 'Δεν υπάρχουν βάρδιες διαθέσιμες' : 'Δεν υπάρχουν κρατήσεις για αυτή την ημερομηνία'}</p>
      </div>
    );
  }

  const handlePreviousDay = () => {
    if (!onDateChange) return;
    const currentDate = new Date(timelineData.date);
    currentDate.setDate(currentDate.getDate() - 1);
    const newDate = currentDate.toISOString().split('T')[0];
    onDateChange(newDate);
  };

  const handleNextDay = () => {
    if (!onDateChange) return;
    const currentDate = new Date(timelineData.date);
    currentDate.setDate(currentDate.getDate() + 1);
    const newDate = currentDate.toISOString().split('T')[0];
    onDateChange(newDate);
  };

  return (
    <div className="space-y-4">
      {/* Date Header — hidden on mobile since the compact toolbar above already shows date/shift */}
      <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 min-w-0">
        <div className="flex items-center justify-center sm:justify-start gap-2 min-w-0 flex-1">
          {onDateChange && (
            <button
              onClick={handlePreviousDay}
              className="p-2 hover:bg-accent rounded-md transition-colors touch-target flex-shrink-0"
              title="Προηγούμενη ημέρα"
            >
              <ChevronLeft className="h-5 w-5 text-foreground" />
            </button>
          )}
          <h2 className="text-base sm:text-xl font-semibold truncate min-w-0" title={format(new Date(timelineData.date), "EEEE, d MMMM yyyy", { locale: el })}>
            {format(new Date(timelineData.date), "EEEE, d MMMM", { locale: el })}
          </h2>
          {onDateChange && (
            <button
              onClick={handleNextDay}
              className="p-2 hover:bg-accent rounded-md transition-colors touch-target flex-shrink-0"
              title="Επόμενη ημέρα"
            >
              <ChevronRight className="h-5 w-5 text-foreground" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-center sm:justify-start gap-2 flex-shrink-0 text-sm text-muted-foreground">
          <Clock className="h-4 w-4 flex-shrink-0" />
          <span>
            {currentShift.shift.name} ({currentShift.shift.start_time?.slice(0, 5)}–{currentShift.shift.end_time?.slice(0, 5)})
          </span>
        </div>
      </div>

      {/* Horizontal Timeline Layout */}
      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="flex">
          {/* Fixed Time Column */}
          <div className="w-[4.5rem] md:w-20 border-r border-border bg-accent/50">
            <div className="h-12 border-b border-border flex items-center justify-center">
              <h3 className="font-medium text-sm text-muted-foreground">Ώρα</h3>
            </div>
            {currentShift.timeSlots.map((slot) => (
              <div key={slot.time} className="h-20 border-b border-border last:border-b-0 flex items-center justify-center">
                <span className="text-sm font-mono font-medium">{slot.time}</span>
              </div>
            ))}
          </div>

          {/* Scrollable Reservations Area */}
          <div className="flex-1 min-w-0">
            <div className="h-12 border-b border-border bg-accent/50 flex items-center justify-center">
              <h3 className="font-medium text-sm text-muted-foreground">Κρατήσεις</h3>
            </div>
            <div className="relative">
              {currentShift.timeSlots.map((slot) => (
                <div key={slot.time} className="h-20 border-b border-border last:border-b-0 relative">
                  <ScrollableTimeSlot
                    timeSlot={slot}
                    onEditReservation={onEditReservation}
                    onDeleteReservation={onDeleteReservation}
                    onCheckInReservation={onCheckInReservation}
                    onCompleteReservation={onCompleteReservation}
                    onMarkNoShow={onMarkNoShow}
                    onLongPressReservation={openActionSheet}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>


      {/* Mobile Action Sheet for long-press - Using Portal to render at body level */}
      {actionSheetReservation && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/50 dark:bg-black/70 flex items-end justify-center md:items-center"
          onClick={closeActionSheet}
        >
          <div
            className="w-full max-w-md bg-card border-t border-border rounded-t-2xl md:rounded-2xl md:border shadow-lg overflow-hidden pb-[calc(env(safe-area-inset-bottom,0px)+5rem)] md:pb-4 md:max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Scrollable content area */}
            <div className="max-h-[70vh] overflow-y-auto p-4 space-y-3">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div
                    className={cn(
                      "text-sm font-semibold",
                      actionSheetReservation.status === "in-progress" && "text-amber-600 dark:text-amber-400",
                      actionSheetReservation.status === "confirmed" && "text-blue-600 dark:text-blue-400",
                      actionSheetReservation.status === "completed" && "text-green-600 dark:text-green-400",
                      actionSheetReservation.status === "cancelled" && "text-red-600 dark:text-red-400",
                      actionSheetReservation.status === "no-show" && "text-gray-600 dark:text-gray-400"
                    )}
                  >
                    {getStatusText(actionSheetReservation.status)}
                  </div>
                  <div className="font-semibold text-lg text-foreground flex items-center gap-1">
                    {actionSheetReservation.customer?.name || "Unknown Customer"}
                    {/* VIP badge placeholder - add when field is available */}
                  </div>
                  <div className="text-sm text-muted-foreground flex gap-2 flex-wrap">
                    <span>{actionSheetReservation.time}</span>
                    <span>• {actionSheetReservation.party_size} άτομα</span>
                    {getReservationTableLabel(actionSheetReservation) && (
                      <span>• {getReservationTableLabel(actionSheetReservation)}</span>
                    )}
                  </div>
                  
                  {/* Phone number */}
                  {getCustomerPhoneForDisplay(actionSheetReservation.customer) && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <Phone className="h-3 w-3" />
                      <span>{getCustomerPhoneForDisplay(actionSheetReservation.customer)}</span>
                    </div>
                  )}
                  
                  {/* Source */}
                  {actionSheetReservation.source && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      {(() => {
                        const SourceIcon = SOURCE_ICONS[actionSheetReservation.source as ReservationSource];
                        return <SourceIcon className="h-3 w-3" />;
                      })()}
                      <span>{SOURCE_LABELS[actionSheetReservation.source as ReservationSource]}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={closeActionSheet}
                  className="text-sm text-muted-foreground hover:text-foreground flex-shrink-0 transition-colors"
                >
                  Κλείσιμο
                </button>
              </div>

              {/* Reservation Notes */}
              {getReservationNotesForDisplay(actionSheetReservation.notes) && (
                <div className="rounded-lg bg-muted p-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <MessageSquare className="h-4 w-4" />
                    <span>Σημειώσεις Κράτησης</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getReservationNotesForDisplay(actionSheetReservation.notes)}
                  </p>
                </div>
              )}

              {/* Customer Notes */}
              {getCustomerNotesForDisplay(actionSheetReservation.customer?.notes) && (
                <div className="rounded-lg bg-accent p-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-accent-foreground">
                    <User className="h-4 w-4" />
                    <span>Σημειώσεις Πελάτη</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getCustomerNotesForDisplay(actionSheetReservation.customer?.notes)}
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="space-y-2 pt-2">
              {(() => {
                const res = actionSheetReservation;
                const items: Array<{ label: string; onClick?: () => void; show: boolean; tone?: 'danger' | 'success' | 'primary' | 'complete' | 'reopen' | 'noshow' }> = [
                  {
                    label: "Check-in (Σε εξέλιξη)",
                    onClick: () => { onCheckInReservation?.(res.id); closeActionSheet(); },
                    show: res.status === "confirmed" && !!onCheckInReservation,
                    tone: 'success',
                  },
                  {
                    label: "Δεν εμφανίστηκε",
                    onClick: () => { onMarkNoShow?.(res.id); closeActionSheet(); },
                    show: res.status === "confirmed" && shouldShowNoShow(res) && !!onMarkNoShow,
                    tone: 'noshow',
                  },
                  {
                    label: "Ολοκληρώθηκε",
                    onClick: () => { onCompleteReservation?.(res.id); closeActionSheet(); },
                    show: res.status === "in-progress" && !!onCompleteReservation,
                    tone: 'complete',
                  },
                  {
                    label: "Επαναφορά",
                    onClick: () => { onReopenReservation?.(res.id); closeActionSheet(); },
                    show: ["in-progress", "completed", "cancelled", "no-show"].includes(res.status) && !!onReopenReservation,
                    tone: 'reopen',
                  },
                  {
                    label: "Επεξεργασία",
                    onClick: () => { onEditReservation?.(res); closeActionSheet(); },
                    show: !!onEditReservation,
                    tone: 'primary',
                  },
                  {
                    label: "Διαγραφή",
                    onClick: () => { onDeleteReservation?.(res.id); closeActionSheet(); },
                    show: !!onDeleteReservation && !["cancelled", "completed", "no-show"].includes(res.status),
                    tone: 'danger',
                  },
                ];
                return items
                  .filter(i => i.show)
                  .map((item, idx) => (
                    <button
                      key={idx}
                      onClick={item.onClick}
                      className={cn(
                        "w-full text-left px-4 py-3 rounded-lg border transition-colors touch-target",
                        item.tone === 'danger'
                          ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/50 dark:text-red-400 dark:hover:bg-red-900/60"
                          : item.tone === 'success'
                          ? "border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-800 dark:bg-green-950/50 dark:text-green-400 dark:hover:bg-green-900/60"
                          : item.tone === 'primary'
                          ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-400 dark:hover:bg-blue-900/60"
                          : item.tone === 'complete'
                          ? "border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/50 dark:text-purple-400 dark:hover:bg-purple-900/60"
                          : item.tone === 'reopen'
                          ? "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-400 dark:hover:bg-sky-900/60"
                          : item.tone === 'noshow'
                          ? "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-950/50 dark:text-gray-300 dark:hover:bg-gray-900/60"
                          : "border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground"
                      )}
                    >
                      <span className="inline-flex items-center gap-2">
                        {item.tone === 'reopen' && <RotateCcw className="h-4 w-4" />}
                        {item.label}
                      </span>
                    </button>
                  ));
              })()}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
